import { useReducer, useCallback, useEffect, useRef, useState } from 'react';
import { get, post, getErrorMessage } from '../utils/api';
import { CTPDailyResponse, CTPState, CTPAction, CTPAnsweredQuestion } from '../types/compareThePair';
import { useAuth } from '../hooks/useAuth';

const STORAGE_KEY_PREFIX = 'wallstreetle-ctp-';

function getStorageKey(gameDate: string): string {
  return `${STORAGE_KEY_PREFIX}${gameDate}`;
}

interface SavedResult {
  score: number;
  answers: boolean[];
  answeredQuestions: CTPAnsweredQuestion[];
  gameDate: string;
  vote?: 'A' | 'B';
}

function loadSavedResult(gameDate: string): SavedResult | null {
  try {
    const raw = localStorage.getItem(getStorageKey(gameDate));
    if (!raw) return null;
    return JSON.parse(raw) as SavedResult;
  } catch {
    return null;
  }
}

function saveResult(gameDate: string, score: number, answers: boolean[], answeredQuestions: CTPAnsweredQuestion[]): void {
  try {
    const key = getStorageKey(gameDate);
    const raw = localStorage.getItem(key);
    const existing = raw ? (JSON.parse(raw) as SavedResult) : null;
    localStorage.setItem(
      key,
      JSON.stringify({ score, answers, answeredQuestions, gameDate, ...(existing?.vote ? { vote: existing.vote } : {}) })
    );
  } catch {
    // localStorage may be full
  }
}

const initialState: CTPState = {
  status: 'idle',
  data: null,
  currentIndex: 0,
  score: 0,
  answers: [],
  answeredQuestions: [],
  lastAnswer: null,
  expandedInsight: null,
  error: null,
};

function reducer(state: CTPState, action: CTPAction): CTPState {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, status: 'loading', error: null };
    case 'LOADED':
      return {
        ...state,
        status: 'ready',
        data: action.data,
        currentIndex: 0,
        score: 0,
        answers: [],
        answeredQuestions: [],
        lastAnswer: null,
      };
    case 'START_GAME':
      if (state.status !== 'ready') return state;
      return { ...state, status: 'playing' };
    case 'ANSWER': {
      if (!state.data) return state;
      const question = state.data.questions[state.currentIndex];
      if (!question) return state;
      const correct = action.choice === question.correctAnswer;
      const answeredQ: CTPAnsweredQuestion = {
        question,
        userChoice: action.choice,
        correct,
      };
      return {
        ...state,
        status: 'revealed',
        score: correct ? state.score + 1 : state.score,
        answers: [...state.answers, correct],
        answeredQuestions: [...state.answeredQuestions, answeredQ],
        lastAnswer: action.choice,
      };
    }
    case 'NEXT_QUESTION': {
      const nextIndex = state.currentIndex + 1;
      const totalQuestions = state.data?.questions.length || 10;
      if (nextIndex >= totalQuestions) {
        return { ...state, status: 'voting' };
      }
      // If next question was already answered (reviewing), show it revealed
      if (nextIndex < state.answeredQuestions.length) {
        const reviewed = state.answeredQuestions[nextIndex];
        return { ...state, status: 'revealed', currentIndex: nextIndex, lastAnswer: reviewed.userChoice };
      }
      return { ...state, status: 'playing', currentIndex: nextIndex, lastAnswer: null };
    }
    case 'PREV_QUESTION': {
      // From voting slide: go back to the last metric question
      if (state.status === 'voting') {
        const lastAnswer = state.answeredQuestions[state.currentIndex];
        if (!lastAnswer) return state;
        return {
          ...state,
          status: 'revealed',
          lastAnswer: lastAnswer.userChoice,
        };
      }
      if (state.currentIndex <= 0) return state;
      const prevIdx = state.currentIndex - 1;
      const prevAnswer = state.answeredQuestions[prevIdx];
      if (!prevAnswer) return state;
      return {
        ...state,
        status: 'revealed',
        currentIndex: prevIdx,
        lastAnswer: prevAnswer.userChoice,
      };
    }
    case 'SHOW_VOTE':
      return { ...state, status: 'voting' };
    case 'VOTE_DONE':
      return { ...state, status: 'complete' };
    case 'REVIEW': {
      if (state.status !== 'complete') return state;
      const lastIdx = state.answeredQuestions.length - 1;
      const last = state.answeredQuestions[lastIdx];
      return {
        ...state,
        status: 'revealed',
        currentIndex: lastIdx,
        lastAnswer: last?.userChoice ?? null,
      };
    }
    case 'COMPLETE':
      return { ...state, status: 'complete' };
    case 'RESTORE':
      return {
        ...state,
        status: 'complete',
        score: action.score,
        answers: action.answers,
        answeredQuestions: action.answeredQuestions,
      };
    case 'TOGGLE_INSIGHT':
      return {
        ...state,
        expandedInsight: state.expandedInsight === action.index ? null : action.index,
      };
    case 'SET_ERROR':
      return { ...state, status: 'idle', error: action.error };
    default:
      return state;
  }
}

export function useCompareThePair() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useAuth();
  const savedRef = useRef(false);
  const [savedVote, setSavedVote] = useState<'A' | 'B' | null>(null);

  // Fetch daily pair on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchDaily() {
      dispatch({ type: 'START_LOADING' });
      try {
        const response = await get<CTPDailyResponse>('/api/compare-the-pair/daily');
        if (cancelled) return;
        const data = response.data;

        // Check if already completed today
        const saved = loadSavedResult(data.gameDate);
        if (saved) {
          // Restore answered questions from saved data, or rebuild from questions + answers
          const answeredQuestions = saved.answeredQuestions?.length
            ? saved.answeredQuestions
            : data.questions.map((q, i) => ({
                question: q,
                userChoice: (saved.answers[i] ? q.correctAnswer : (q.correctAnswer === 'A' ? 'B' : 'A')) as 'A' | 'B',
                correct: saved.answers[i],
              }));
          dispatch({ type: 'LOADED', data });
          dispatch({ type: 'RESTORE', score: saved.score, answers: saved.answers, answeredQuestions });
          if (saved.vote) {
            setSavedVote(saved.vote);
          } else {
            // No vote yet — show vote slide instead of results
            dispatch({ type: 'SHOW_VOTE' });
          }
          return;
        }

        dispatch({ type: 'LOADED', data });
      } catch (err) {
        if (!cancelled) {
          dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
        }
      }
    }

    fetchDaily();
    return () => { cancelled = true; };
  }, []);

  // Save result and POST to server when voting (game just finished) or complete (restored with vote)
  useEffect(() => {
    if ((state.status !== 'voting' && state.status !== 'complete') || !state.data || savedRef.current) return;

    savedRef.current = true;
    saveResult(state.data.gameDate, state.score, state.answers, state.answeredQuestions);

    // Fire-and-forget POST to save result (only for authenticated users)
    if (user) {
      post('/api/compare-the-pair/result', {
        gameDate: state.data.gameDate,
        score: state.score,
        answers: state.answers,
      }).catch(() => {});
    }
  }, [state.status, state.data, state.score, state.answers, state.answeredQuestions, user]);

  const answer = useCallback((choice: 'A' | 'B') => {
    if (state.status !== 'playing') return;
    dispatch({ type: 'ANSWER', choice });
  }, [state.status]);

  const nextQuestion = useCallback(() => {
    if (state.status !== 'revealed') return;
    dispatch({ type: 'NEXT_QUESTION' });
  }, [state.status]);

  const startGame = useCallback(() => {
    dispatch({ type: 'START_GAME' });
    // Fire-and-forget analytics
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${apiBaseUrl}/api/analytics/start-ctp-click`, { method: 'POST' }).catch(() => {});
  }, []);

  const toggleInsight = useCallback((index: number) => {
    dispatch({ type: 'TOGGLE_INSIGHT', index });
  }, []);

  const prevQuestion = useCallback(() => {
    dispatch({ type: 'PREV_QUESTION' });
  }, []);

  const completeVote = useCallback(() => {
    dispatch({ type: 'VOTE_DONE' });
  }, []);

  const reviewQuestions = useCallback(() => {
    dispatch({ type: 'REVIEW' });
  }, []);

  const saveVote = useCallback((vote: 'A' | 'B') => {
    if (!state.data) return;
    setSavedVote(vote);
    try {
      const key = getStorageKey(state.data.gameDate);
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as SavedResult;
        parsed.vote = vote;
        localStorage.setItem(key, JSON.stringify(parsed));
      }
    } catch {
      // localStorage may be full
    }
    // Fire-and-forget POST to save vote
    post('/api/compare-the-pair/vote', { gameDate: state.data.gameDate, vote }).catch(() => {});
  }, [state.data]);

  const currentQuestion = state.data?.questions[state.currentIndex] ?? null;

  return {
    state,
    currentQuestion,
    answer,
    nextQuestion,
    prevQuestion,
    startGame,
    toggleInsight,
    savedVote,
    saveVote,
    completeVote,
    reviewQuestions,
  };
}
